const CAP20 = {
  id: "fundamentos-blockchain-20",
  slug: "por-que-se-crearon-los-tokens-erc20",
  title: "¿Por qué se crearon los Tokens ERC-20?",
  sections: [
    {
      id: "hero",
      heading: "El Origen de un Estándar Universal en Ethereum",
      text: `Los tokens **ERC-20** surgieron como respuesta a un problema estructural dentro del ecosistema Ethereum: 
la falta de un lenguaje común entre los distintos activos digitales.  
Antes de su creación, cada desarrollador implementaba sus propios contratos de token con lógicas y nombres de funciones diferentes, 
lo que generaba incompatibilidad entre billeteras, intercambios y aplicaciones descentralizadas (dApps).  
El ERC-20 nació para establecer una **interfaz uniforme, reutilizable y segura**, 
capaz de garantizar que todos los tokens funcionaran bajo las mismas reglas básicas dentro de la red.`
    },
    {
      id: "motivacion",
      heading: "Motivaciones Principales para su Creación",
      text: `El propósito central del estándar fue crear una base de comunicación y operación común 
que facilitara el desarrollo, la integración y la interoperabilidad de tokens en la red Ethereum. 
A continuación se describen las motivaciones clave que impulsaron su diseño:`,
      list: [
        {
          title: "1. Uniformidad en la programación",
          text: `El ERC-20 define un conjunto fijo de funciones (transfer, approve, balanceOf, etc.) 
que todos los tokens deben implementar.  
Esta estandarización proporciona una **interfaz estable y predecible**, 
permitiendo que cualquier aplicación —una billetera, un intercambio o una dApp— 
pueda interactuar con cualquier token sin tener que modificar su código fuente.`
        },
        {
          title: "2. Reutilización de código y reducción de errores",
          text: `Antes del estándar, cada proyecto debía crear su propio contrato desde cero, 
repitiendo código y aumentando el riesgo de vulnerabilidades.  
El ERC-20 permitió **reutilizar una misma plantilla de contrato inteligente**, 
probada y auditada, reduciendo drásticamente los errores de implementación 
y mejorando la seguridad general del ecosistema.`
        },
        {
          title: "3. Interfaz de programación de aplicaciones (API) común",
          text: `El estándar introdujo una API (Application Programming Interface) compartida que 
permite a los desarrolladores trabajar con tokens desde distintos lenguajes de programación como **Solidity**, **JavaScript**, **Python** o **Rust**.  
Esta interoperabilidad de código hizo posible la creación de herramientas universales como MetaMask, 
Ethers.js o Web3.js, que hoy son esenciales para la interacción con contratos inteligentes.`
        },
        {
          title: "4. Compatibilidad entre monederos y exchanges",
          text: `Gracias al ERC-20, las billeteras digitales pueden reconocer automáticamente 
los tokens que siguen el mismo estándar, sin requerir configuración manual.  
Esto simplificó enormemente el comercio y el almacenamiento de activos, 
favoreciendo la expansión de los **intercambios descentralizados (DEX)** y la adopción global.`
        },
        {
          title: "5. Seguridad en la interacción entre contratos",
          text: `El estándar también se diseñó para reducir el riesgo de romper contratos al actualizar o migrar tokens.  
Al mantener una estructura común, los desarrolladores pueden realizar mejoras o integraciones 
sin alterar el comportamiento de los contratos existentes, preservando la **compatibilidad hacia atrás (backward compatibility)**.`
        }
      ]
    },
    {
      id: "impacto-tecnico",
      heading: "Impacto Técnico y Social del Estándar ERC-20",
      text: `Más allá de su importancia técnica, el ERC-20 marcó un punto de inflexión en la economía digital 
y en la forma en que las comunidades participan en proyectos descentralizados.`,
      list: [
        {
          title: "Base para la tokenización moderna",
          text: `El ERC-20 hizo posible representar cualquier valor —dinero, energía, reputación, puntos de fidelidad o acciones— 
en forma de token digital.  
Esta capacidad de **tokenización universal** abrió la puerta a nuevas economías digitales 
y modelos de participación compartida.`
        },
        {
          title: "Impulso a las ICO y a la financiación descentralizada",
          text: `Entre 2016 y 2018, el estándar ERC-20 permitió a cientos de startups emitir sus propios tokens 
y recaudar fondos a través de **Initial Coin Offerings (ICO)**, sin depender de intermediarios financieros tradicionales.  
Este fenómeno popularizó el concepto de propiedad distribuida y democratizó la inversión tecnológica.`
        },
        {
          title: "Puente entre desarrolladores y usuarios",
          text: `El estándar facilitó la comprensión del funcionamiento de los tokens y redujo la barrera técnica de entrada.  
Cualquier persona con conocimientos básicos de programación puede crear su propio token ERC-20, 
comprender su API y adaptarlo a nuevos propósitos sociales, educativos o comerciales.`
        },
        {
          title: "Fomento de la transparencia y la confianza",
          text: `Al basarse en una interfaz pública y abierta, el ERC-20 garantiza que todas las operaciones —transferencias, aprobaciones y saldos— 
sean verificables en la blockchain.  
Esto fortaleció la **confianza del usuario** y consolidó el principio de transparencia que distingue a Ethereum.`
        }
      ]
    },
    {
      id: "evolucion",
      heading: "Evolución y Relevancia Actual del ERC-20",
      text: `Aunque el estándar ERC-20 fue creado en 2015, sigue siendo en 2025 uno de los pilares técnicos del mundo blockchain.  
Ha inspirado la creación de otros estándares más avanzados (como ERC-721 y ERC-1155), 
pero continúa siendo la referencia para los tokens fungibles.`,
      list: [
        {
          title: "Actualizaciones e integraciones modernas",
          text: `Los nuevos contratos ERC-20 implementan mejoras de seguridad como **permit()**, 
que permite aprobar transacciones sin necesidad de gastar gas adicional, 
y compatibilidad con protocolos *Layer 2* (Arbitrum, Optimism, Polygon) para reducir costos.`
        },
        {
          title: "Adopción institucional y empresarial",
          text: `Empresas, universidades y organizaciones gubernamentales están adoptando el estándar 
para emitir tokens de certificación, microcredenciales, stablecoins o sistemas de recompensas transparentes.`
        },
        {
          title: "Sostenibilidad y futuro del estándar",
          text: `El ERC-20 ha demostrado ser un modelo de longevidad tecnológica.  
Su equilibrio entre simplicidad y robustez permite que siga vigente en entornos corporativos, educativos y financieros, 
incluso con la aparición de nuevas redes blockchain.`
        }
      ]
    },
    {
      id: "conclusion",
      heading: "Conclusión",
      text: `Los tokens ERC-20 fueron creados para resolver un problema de comunicación y estandarización, 
pero su impacto trascendió lo técnico.  
Hoy representan la base de la **interoperabilidad, seguridad y confianza** que define al ecosistema Ethereum.  
Su estructura clara, su API universal y su capacidad de integrarse en múltiples lenguajes 
han permitido la expansión de una economía descentralizada global.  
En definitiva, el ERC-20 no solo es un estándar de código, sino un **puente entre innovación, adopción y colaboración humana.**`
    }
  ]
};

export default CAP20;
